import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';

import { useSyncedPref } from '#hooks/useSyncedPref';
import { registrarUso } from '#uso';

import {
  cuentaComoHecha,
  diaLocal,
  guardarCopia,
  nombreArchivoCopia,
} from './copia';
import type { NavegadorCompartir, ResultadoGuardado } from './copia';

export type ResultadoCopia = ResultadoGuardado | 'error';
export type OrigenCopia = 'ajustes' | 'inicio-widget' | 'inicio-ancla';

type Preparada = { archivo: File; datos: Uint8Array };

/**
 * Hacer la copia de seguridad (el .zip de «Exportar») y apuntar el día en la
 * preferencia sincronizada `copia-ultima-fecha`, para que se vea igual en
 * cualquier dispositivo. Uso: solo eventos sin datos (`copia_hecha`…).
 */
export function useCopiaSeguridad(origen: OrigenCopia) {
  const { t } = useTranslation();
  const [ultima, setUltima] = useSyncedPref('copia-ultima-fecha');
  // El archivo ya preparado: si cancela la hoja de compartir, «Descargar» no
  // vuelve a exportar.
  const preparada = useRef<Preparada | null>(null);

  const preparar = useCallback(async (): Promise<Preparada | null> => {
    const respuesta = await send('export-budget');
    if (('error' in respuesta && respuesta.error) || !respuesta.data) {
      return null;
    }
    const datos = respuesta.data as unknown as Uint8Array;
    const archivo = new File(
      [datos as BlobPart],
      nombreArchivoCopia(new Date()),
      { type: 'application/zip' },
    );
    preparada.current = { archivo, datos };
    return preparada.current;
  }, []);

  const apuntar = useCallback(
    (resultado: ResultadoCopia) => {
      if (resultado !== 'error' && cuentaComoHecha(resultado)) {
        setUltima(diaLocal(new Date()));
        registrarUso('copia_hecha', { origen, resultado });
      } else if (resultado === 'error') {
        registrarUso('copia_error', { origen });
      } else {
        registrarUso('copia_no_guardada', { origen, resultado });
      }
    },
    [origen, setUltima],
  );

  const descargarPreparada = useCallback(
    async (p: Preparada) => {
      await window.Actual.saveFile(
        p.datos as unknown as Buffer,
        p.archivo.name,
        t('Make a backup'),
      );
    },
    [t],
  );

  /** Prepara el .zip y abre la hoja de compartir (o lo descarga). */
  const hacerCopia = useCallback(async (): Promise<ResultadoCopia> => {
    let resultado: ResultadoCopia;
    try {
      const p = await preparar();
      if (!p) {
        resultado = 'error';
      } else {
        resultado = await guardarCopia({
          archivo: p.archivo,
          titulo: t('Rumbo backup'),
          nav: navigator as unknown as NavegadorCompartir,
          descargar: () => descargarPreparada(p),
        });
      }
    } catch {
      resultado = 'error';
    }
    apuntar(resultado);
    return resultado;
  }, [apuntar, descargarPreparada, preparar, t]);

  /** Descarga el archivo (el ya preparado, o uno nuevo). */
  const descargar = useCallback(async (): Promise<ResultadoCopia> => {
    let resultado: ResultadoCopia;
    try {
      const p = preparada.current ?? (await preparar());
      if (!p) {
        resultado = 'error';
      } else {
        await descargarPreparada(p);
        resultado = 'descargada';
      }
    } catch {
      resultado = 'error';
    }
    apuntar(resultado);
    return resultado;
  }, [apuntar, descargarPreparada, preparar]);

  return { ultima, hacerCopia, descargar };
}
